import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketing } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Loader2, Heart, MessageCircle, Repeat, MapPin, Plus, X, Home } from 'lucide-react';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

// Member's own "My Interests" section — every survey they've completed +
// re-take button. Also surfaces onboarding-survey interests + location
// preferences that drive the personalized banner slider.
export default function MyInterests() {
  const [rows, setRows] = useState(null);
  const { user, refresh } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  const [newCity, setNewCity] = useState('');
  const [newState, setNewState] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    marketing.mySurveys().then(setRows).catch(() => setRows([]));
  }, []);

  const addCity = async () => {
    if (!newCity.trim() || !newState) { toast({ title: 'City and state are required' }); return; }
    setBusy(true);
    try {
      const r = await marketing.addLocation(newCity.trim(), newState);
      if (r.already) toast({ title: r.reason === 'matches_home' ? 'That\'s already your home city' : 'City already added' });
      else toast({ title: `Added ${newCity.trim()}, ${newState}` });
      await refresh();
      setNewCity(''); setNewState(''); setAddOpen(false);
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  const removeCity = async (loc) => {
    if (!window.confirm(`Remove ${loc.city}, ${loc.state}?`)) return;
    try { await marketing.removeLocation(loc.city, loc.state); await refresh(); toast({ title: 'Removed' }); }
    catch { toast({ title: 'Remove failed' }); }
  };

  if (rows === null) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  const onb = user?.onboardingSurvey || {};
  const interests = onb.interests || [];
  const lifestyle = onb.lifestyle || [];
  const home = onb.homeLocation || null;
  const secondary = onb.additionalLocations || [];

  return (
    <div className="space-y-5" data-testid="my-interests-page">
      <div>
        <h1 className="text-2xl font-bold inline-flex items-center gap-2"><Heart size={22} className="text-pink-400"/> My Interests</h1>
        <p className="text-gray-400 text-sm">All your survey answers live here. They power personalized deals and the banners you see on the homepage.</p>
      </div>

      <section className="pbb-card p-5" data-testid="my-interests-locations">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-white inline-flex items-center gap-2"><MapPin size={14} className="text-green-400"/> My locations</div>
          {!addOpen && (
            <button onClick={() => setAddOpen(true)} data-testid="add-city-btn"
              className="text-xs px-2.5 py-1 rounded-lg border border-green-500/40 text-green-200 hover:bg-green-500/10 inline-flex items-center gap-1">
              <Plus size={11}/> Add a city
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400">Home city gets first priority in your banner feed. Add cities you travel to or care about — they'll appear next.</p>
        <div className="mt-3 space-y-1.5">
          {home?.city ? (
            <div className="flex items-center gap-2 text-sm text-gray-200" data-testid="location-home">
              <Home size={12} className="text-yellow-400"/> <span className="text-white font-semibold">{home.city}, {home.state}</span>
              <span className="text-[10px] text-yellow-300 uppercase tracking-widest">Home</span>
            </div>
          ) : (
            <div className="text-xs text-gray-500">No home city set. Complete onboarding to add one.</div>
          )}
          {secondary.map((loc, i) => (
            <div key={`${loc.city}-${loc.state}-${i}`} data-testid={`location-secondary-${i}`}
              className="flex items-center gap-2 text-sm text-gray-200">
              <MapPin size={12} className="text-green-400"/> {loc.city}, {loc.state}
              <button onClick={() => removeCity(loc)} data-testid={`location-remove-${i}`}
                className="ml-auto text-[11px] text-gray-500 hover:text-red-400 inline-flex items-center gap-0.5">
                <X size={11}/> remove
              </button>
            </div>
          ))}
        </div>
        {addOpen && (
          <div className="mt-3 pt-3 border-t border-[#1a1a1a] flex gap-2 items-end" data-testid="add-city-row">
            <div className="flex-1">
              <div className="text-[11px] text-gray-500 mb-1">City</div>
              <input value={newCity} onChange={e => setNewCity(e.target.value)}
                placeholder="e.g. Houston" maxLength={80}
                data-testid="add-city-input"
                className="pbb-input"/>
            </div>
            <div>
              <div className="text-[11px] text-gray-500 mb-1">State</div>
              <select value={newState} onChange={e => setNewState(e.target.value)}
                data-testid="add-city-state" className="pbb-input">
                <option value="">Select</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <button onClick={addCity} disabled={busy} data-testid="add-city-submit"
              className="pbb-btn-green inline-flex items-center gap-1">
              {busy ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>} Add
            </button>
            <button type="button" onClick={() => setAddOpen(false)} data-testid="add-city-cancel"
              className="text-xs px-2.5 py-2 rounded-lg border border-[#2a2a2a] text-gray-300 hover:border-gray-500">Cancel</button>
          </div>
        )}
      </section>

      <section className="pbb-card p-5">
        <div className="text-sm font-semibold text-white">Your preferences</div>
        <p className="text-xs text-gray-400 mt-1">From your onboarding survey.</p>
        {interests.length + lifestyle.length === 0 ? (
          <p className="text-xs text-gray-500 mt-2">Nothing set yet. Complete your onboarding survey next time it appears, or tap the profile icon to edit.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {interests.length > 0 && (
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider">Interests</div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {interests.map(i => <span key={i} data-testid="interest-chip" className="text-xs px-2 py-1 rounded-full bg-green-500/10 border border-green-500/30 text-green-200">{i}</span>)}
                </div>
              </div>
            )}
            {lifestyle.length > 0 && (
              <div>
                <div className="text-[11px] text-gray-500 uppercase tracking-wider">Lifestyle</div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {lifestyle.map(i => <span key={i} data-testid="lifestyle-chip" className="text-xs px-2 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-200">{i}</span>)}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section>
        <div className="text-sm font-semibold text-white mb-2">Completed business surveys</div>
        {rows.length === 0 ? (
          <div className="pbb-card p-6 text-center" data-testid="my-interests-empty">
            <MessageCircle className="mx-auto text-gray-500" size={22}/>
            <div className="mt-2 text-sm text-gray-300">No surveys yet</div>
            <p className="text-xs text-gray-500 mt-1">When businesses share surveys with you, your answers show up here — and you'll earn +50 loyalty pts each time.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="my-interests-list">
            {rows.map(r => (
              <li key={r.survey.id} className="pbb-card p-4" data-testid={`my-interests-row-${r.survey.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-widest text-gray-500">{r.business?.name || 'A business'}</div>
                    <div className="text-white font-semibold">{r.survey.title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">Answered {new Date(r.response.createdAt).toLocaleDateString()} · {(r.response.answers || []).length} answers</div>
                  </div>
                  <Link to={`/s/${r.survey.slug}`} target="_blank" data-testid={`my-interests-retake-${r.survey.id}`}
                    className="text-xs px-2.5 py-1 rounded-lg border border-yellow-500/40 text-yellow-200 hover:bg-yellow-500/10 inline-flex items-center gap-1">
                    <Repeat size={11}/> Retake
                  </Link>
                </div>
                <ul className="space-y-1 mt-3 pt-3 border-t border-[#1a1a1a] text-sm">
                  {(r.response.answers || []).map((a, i) => {
                    const q = (r.survey.questions || []).find(qq => qq.id === a.questionId);
                    return (
                      <li key={i}>
                        <div className="text-[11px] text-gray-500 uppercase tracking-wider">{q?.prompt || '(unknown)'}</div>
                        <div className="text-gray-200">{Array.isArray(a.value) ? a.value.join(', ') : String(a.value ?? '—')}</div>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
